import { faDesktop } from '@fortawesome/free-solid-svg-icons/faDesktop';
import { faMobileScreenButton } from '@fortawesome/free-solid-svg-icons/faMobileScreenButton';
import { faTabletScreenButton } from '@fortawesome/free-solid-svg-icons/faTabletScreenButton';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Modal } from '../../../components/modal/Modal';
import { AtiraLinkThemes } from '../../../model/atira-link/types/AtiraLinkThemes.enum';
import { atiraLinkSliceSelectors } from '../../../redux/atira-link/atira-link.selector';
import { useAppSelector } from '../../../redux/store';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { AtiraLinkPreviewPhone } from '../../my-link/components/AtiraLinkPreviewPhone';
import { TemplatePreviewMode } from '../model/TemplatePreviewMode.enum';

const Wrapper = styled(Flex)`
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${Spacing.s};
`;

const StyledModal = styled(Modal)`
  .ant-modal-content,
  .ant-modal-title,
  .ant-modal-header,
  .ant-modal-title > div {
    background-color: transparent !important;
  }

  .ant-modal-header {
    position: fixed;
    z-index: 100000;
  }

  .ant-modal-header p,
  .ant-modal-hr {
    display: none;
  }

  .ant-modal-header button {
    margin-inline-start: auto;
    padding: ${Spacing.s};
    border-radius: ${Rounded.md};
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.onMain};
    padding: 0.75rem;
    border: ${({ theme }) => `1px solid ${theme.onMain}`};
  }

  .ant-modal-header button:hover {
    opacity: 0.7;
  }

  .ant-modal-content {
    padding: ${Spacing.s};
    box-shadow: none;
  }
`;

const StyledButton = styled(Button)<{ isSelected: boolean }>`
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.onMain : theme.transparent};
  color: ${({ theme, isSelected }) => (isSelected ? theme.main : theme.onMain)};
  padding: 0.75rem;
  border: ${({ theme }) => `1px solid ${theme.onMain}`};

  &:hover {
    background-color: ${({ theme }) => theme.onMain};
    color: ${({ theme }) => theme.main};
  }
`;

const PreviewModeButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  gap: ${Spacing.m};
  display: none;

  button:nth-child(2),
  button:nth-child(3) {
    display: none;
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    display: flex;
    button:nth-child(2) {
      display: flex;
    }
  }

  @media (min-width: ${Breakpoints.LARGE_DESKTOP}) {
    button:nth-child(3) {
      display: flex;
    }
  }
`;

const PhoneWrapper = styled(Flex)`
  margin-top: ${Spacing.xxl};

  @media (min-width: ${Breakpoints.TABLET}) {
    margin-top: 0;
  }
`;

const ThemedOverlay = createGlobalStyle`
  .ant-modal-mask {
    background-color: ${({ theme }) => theme.main}d1 !important;
    backdrop-filter: blur(20px);
  }
`;

type TemplatePreviewModalProps = {
  selectedTheme?: AtiraLinkThemes | null;
  open: boolean;
  onClose: VoidFunction;
};

export const TemplatePreviewModal: React.FC<TemplatePreviewModalProps> = ({
  selectedTheme,
  onClose,
  open = false,
}) => {
  const [selectedPreviewMode, setSelectedPreviewMode] =
    useState<TemplatePreviewMode>(TemplatePreviewMode.MOBILE);

  const { t } = useTranslation();
  const { pathname } = useLocation();

  let atiraLink = useAppSelector((s) =>
    atiraLinkSliceSelectors.selectThemeDataByName(s, selectedTheme),
  );
  const userAtiraLink = useAppSelector(
    atiraLinkSliceSelectors.selectSelectedAtiraLink,
  );

  atiraLink = pathname.includes('my-link') ? userAtiraLink : atiraLink;

  return (
    <StyledModal
      style={{ top: 0 }}
      title={t('common.preview')}
      open={open}
      onClose={onClose}
      width={'100%'}
    >
      <Wrapper>
        <PreviewModeButtonsWrapper>
          <StyledButton
            isSelected={selectedPreviewMode === TemplatePreviewMode.MOBILE}
            onClick={() => setSelectedPreviewMode(TemplatePreviewMode.MOBILE)}
            icon={faMobileScreenButton}
            iconWidth="lg"
          />

          <StyledButton
            isSelected={selectedPreviewMode === TemplatePreviewMode.TABLET}
            onClick={() => setSelectedPreviewMode(TemplatePreviewMode.TABLET)}
            icon={faTabletScreenButton}
            iconWidth="lg"
          />

          <StyledButton
            isSelected={selectedPreviewMode === TemplatePreviewMode.DESKTOP}
            onClick={() => setSelectedPreviewMode(TemplatePreviewMode.DESKTOP)}
            icon={faDesktop}
            iconWidth="lg"
          />
        </PreviewModeButtonsWrapper>

        <PhoneWrapper>
          <AtiraLinkPreviewPhone
            key={selectedTheme}
            isPreviewMode
            previewMode={selectedPreviewMode}
            values={{
              ...atiraLink,
              theme: selectedTheme || AtiraLinkThemes.BASIC,
            }}
          />
        </PhoneWrapper>
      </Wrapper>

      <ThemedOverlay />
    </StyledModal>
  );
};
